/* eslint-disable camelcase */
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';

import { currencyFormatter } from '../../../utils/formatting';
import DiscountFormatter from '../DiscountFormatter';

const MAXIMUM_RETURN_DESCRIPTION_LENGTH = 25;

const ReturnedItemsVerified = ({
  contactDetails,
  country,
  returnedItems,
  locale,
  sourcePOS
}) => {
  const originalInvoiceNumber = contactDetails?.find(() => true)?.OriginalTaxInvoice?.Number || '';
  const {
    locale: { momentFormat, storeCurrency, storeLocale }, RL_ITEMVOID, RL_OrgDate, RL_OrgStoreNumber, RL_OrgRegNumber, RL_OrgTransaction, RL_Quantity, RL_Reason3, RL_Size,
  } = useSelector((state) => state);
  // eslint-disable-next-line func-names
  const get = function (obj, key) {
    return key.split('.').reduce((acc, cur) => ((typeof acc === 'undefined' || acc === null) ? null : acc[cur]), obj);
  };
  const mapVerfiedReturns = (items) => {
    const result = items.map((item) => {
      const descriptions = item?.Return?.Descriptions?.find((i) => i.TypeCode === 'Short');
      const itemDescription = (() => {
        if (descriptions && descriptions.Text) {
          if (locale === 'en-IN') {
            return descriptions.Text;
          }
          return descriptions.Text.slice(0, MAXIMUM_RETURN_DESCRIPTION_LENGTH);
        }
        return '';
      })();
      const obj = {
        amount: get(item, 'Return.ActualSalesUnitPrice.Amount'),
        description: get(item, 'Return.Reason.Description'),
        endDateTime: get(item, 'Return.TransactionLink.EndDateTime'),
        HSN: get(item, 'Return.Taxes.0.HSNNumber'),
        invoiceNumber: get(item, 'Return.TransactionLink.InvoiceNumber'),
        itemDescription,
        itemId: get(item, 'Return.Item.ID'),
        locationCode: get(item, 'Return.TransactionLink.BusinessUnit.LocationCode'),
        nikeDigitalSizeDescription: get(item, 'Return.NikeDigitalSizeDescription'),
        quantity: get(item, 'Return.Quantity.Quantity'),
        rate: get(item, 'Return.Taxes') ? (item.Return.Taxes.reduce((acc, tax) => acc + (tax.Percent || 0), 0) * 100) : null,
        returnReason: get(item, 'Return.Reason.Description'),
        sequenceNumber: get(item, 'Return.TransactionLink.SequenceNumber'),
        size: get(item, 'Return.MerchandiseHierarchy'),
        taxableFlag: get(item, 'Return.TaxableFlag'),
        UQC: get(item, 'Return.Quantity.UniqueQuantityCode'),
        voidFlag: get(item, 'VoidFlag'),
        workstationId: get(item, 'Return.TransactionLink.Workstation.ID')
      };
      return obj;
    });
    return result;
  };
  const reducedVerfiedReturns = (items) => {
    const result = items.reduce((acc, cur) => {
      const {
        locationCode, workstationId, HSN, rate, UQC, sequenceNumber, invoiceNumber, quantity, endDateTime, ...rest
      } = cur;
      const dupe = acc.find((x) => x.sequenceNumber === sequenceNumber);
      if (dupe) {
        // eslint-disable-next-line fp/no-mutating-methods
        dupe.itemArr.push(rest);
      } else {
        const obj = {
          endDateTime,
          HSN,
          invoiceNumber,
          itemArr: [rest],
          locationCode,
          quantity,
          rate,
          sequenceNumber,
          UQC,
          workstationId
        };
        // eslint-disable-next-line fp/no-mutating-methods
        acc.push(obj);
      }
      return acc;
    }, []);

    return result || [];
  };

  const mapReturns = mapVerfiedReturns(returnedItems);
  const reducedReturns = reducedVerfiedReturns(mapReturns);
  return (
    <>
      {reducedReturns.map((returnObj, index) => (
          // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={`${returnObj.sequenceNumber || 'sequence'}-${index}`}>
          {(index > 0) && (<br />)}
          <article className="justify-between">
            <p>{RL_OrgStoreNumber}: {returnObj.locationCode}</p><p />
          </article>
          <article className="justify-between">
            <p>{RL_OrgRegNumber}: {returnObj.workstationId}</p><p />
          </article>
          <article className="justify-between">
            <p>{RL_OrgTransaction}: {returnObj.sequenceNumber}</p><p />
          </article>
          {locale === 'en-IN' && (
            <article className="justify-between">
              <p>Original Tax Invoice Number: {returnObj.invoiceNumber}</p><p />
            </article>
          )}
          <article className="justify-between">
            <p>{locale === 'en-IN' ? 'Original Tax Invoice Date' : RL_OrgDate}: {moment.utc(returnObj.endDateTime).format(momentFormat)}</p><p />
          </article>
          {locale === 'he-IL' && (
          <article className="justify-between">
            <p>{`חשבונית/קבלה מקורית של מס: ${originalInvoiceNumber}`}</p><p />
          </article>
          )}
          <br /><br />
          {returnObj.itemArr.map((item, itemIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`${returnObj.sequenceNumber}-${item.itemId}-${itemIndex}`}>
              <article className="justify-between">
                <p>{item.voidFlag && (`<${RL_ITEMVOID}>`)}</p>
              </article>
              <article className="justify-between">
                <p className="pl2-sm" style={{ textAlign: 'left' }}>{item?.itemDescription}</p>
                <p>
                  {storeLocale === 'en-IN' ? (
                    <span style={{ whiteSpace: 'nowrap' }}>
                      MRP:
                      <DiscountFormatter isNegative storeLocale={storeLocale}>
                        {currencyFormatter(
                          storeLocale,
                          storeCurrency,
                          item.amount
                        )}
                        {!['CHN', 'MAC', 'HKG', 'TWN', 'IND'].includes(country) && item.taxableFlag ? ' T' : ''}
                      </DiscountFormatter>
                    </span>
                  ) : (
                    <DiscountFormatter isNegative storeLocale={storeLocale}>
                      {currencyFormatter(
                        storeLocale,
                        storeCurrency,
                        item.amount
                      )}
                      {!['CHN', 'MAC', 'HKG', 'TWN', 'IND'].includes(country) && item.taxableFlag ? ' T' : ''}
                    </DiscountFormatter>
                  )}
                </p>
              </article>
              <article className="justify-between">
                <p className="pl2-sm">
                  {item.itemId}&nbsp;&nbsp;
                  {sourcePOS === 'AssistInCloud'
                     ? item.MerchandiseHierarchy?.find((hierarchy1) => hierarchy1.Level === 'Size')?.Code
                      && `${RL_Size}: ${item.MerchandiseHierarchy?.find((hierarchy1) => hierarchy1.Level === 'Size')?.Code}`
                      : item?.nikeDigitalSizeDescription && `${RL_Size}: ${item?.nikeDigitalSizeDescription
                  }`}
                  {storeLocale === 'en-IN' && ` ${RL_Quantity} :${returnObj?.quantity || 1}`}
                </p>
              </article>
              {locale === 'he-IL' && (
              <article className="justify-between">
                <p>{item?.returnReason}</p><p />
              </article>
              )}
              {locale === 'en-IN' && (
                <>
                  <article className="justify-between">
                    {returnObj.HSN && <p className="pl4-sm">{`Rate: ${returnObj.rate}% HSN: ${returnObj.HSN}`}</p>}
                  </article>
                  <article className="justify-between">
                    {returnObj.UQC && <p className="pl4-sm">{`UQC: ${returnObj.UQC}`}</p>}
                  </article>
                </>
              )}
              {country === 'THA'
                ? (
                  <article className="justify-between">
                    <p>{RL_Reason3}</p><p>{(item.description || '')}</p>
                  </article>
                )
                : '' }
              <br />
              <br />
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
    </>
  );
};

ReturnedItemsVerified.defaultProps = {
  contactDetails: [],
  country: '',
  locale: '',
  returnedItems: [],
  sourcePOS: '',
};

ReturnedItemsVerified.propTypes = {
  contactDetails: PropTypes.arrayOf(PropTypes.shape()),
  country: PropTypes.string,
  locale: PropTypes.string,
  returnedItems: PropTypes.arrayOf(PropTypes.shape()),
  sourcePOS: PropTypes.string,
};

export default ReturnedItemsVerified;
